/* Inventory — STORY-028, STORY-029, STORY-030.
 *
 * ── the cost is read, never copied, when a Transaction is named ─────────────
 * An item that names the Transaction that bought it carries no cost of its own:
 * the response reports the Transaction's, and the request refuses to accept one
 * alongside the link. Two answers to one question is what let the two records
 * drift, and the fix is that only one of them can hold the answer.
 *
 * ── the still-out screen has two lists, not one ─────────────────────────────
 * "Every unreturned assignment" files next month's loadout under things to
 * chase, which makes the count the screen exists for wrong. Kit for an Event
 * that has not happened yet is a **packing list**, and it is excluded from the
 * outstanding count. Added 2026-08-10 from the EPIC-006 prototype.
 */
import * as z from 'zod';
/** `glossary.md`, six of them. Names are keys; these are the stored values. */
export const ITEM_CONDITIONS = [
    'excellent',
    'good',
    'fair',
    'lightly_damaged',
    'problematic',
    'lost',
];
/** Conditions that mean somebody should look at the thing. */
export const ATTENTION_CONDITIONS = [
    'problematic',
    'lost',
];
export const ITEM_NAME_MAX = 120;
export const ITEM_NOTES_MAX = 2000;
const itemName = z.string().trim().min(1, 'A name is needed.').max(ITEM_NAME_MAX);
const itemDate = z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'A date, as YYYY-MM-DD.')
    .refine((value) => {
    const [year, month, day] = value.split('-').map(Number);
    if (year === undefined || month === undefined || day === undefined)
        return false;
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return (parsed.getUTCFullYear() === year &&
        parsed.getUTCMonth() === month - 1 &&
        parsed.getUTCDate() === day);
}, 'That is not a date on any calendar.');
export const itemMemberSchema = z.object({
    userId: z.string(),
    displayName: z.string(),
    anonymised: z.boolean(),
});
export const inventoryItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    category: z.string().nullable(),
    /** From the Transaction when one is named, from the item otherwise. */
    purchasedOn: z.string().nullable(),
    costMinor: z.number().int().nullable(),
    costCurrency: z.string().nullable(),
    /**
     * True when the cost above came from the books.
     *
     * The client needs to know, because it must not offer to edit a figure it
     * does not own — and "no cost" and "a cost that lives somewhere else" look
     * identical without this.
     */
    costFromTransaction: z.boolean(),
    transaction: z.object({ id: z.string(), description: z.string() }).nullable(),
    /** Separate fields, equal most of the time — that is why they are separate. */
    owner: itemMemberSchema.nullable(),
    holder: itemMemberSchema.nullable(),
    condition: z.enum(ITEM_CONDITIONS),
    notes: z.string().nullable(),
    createdAt: z.string(),
});
export const inventoryResponseSchema = z.object({
    items: z.array(inventoryItemSchema),
    total: z.number().int(),
});
const costFields = {
    purchasedOn: itemDate.nullish(),
    costMinor: z.number().int().nonnegative('A cost of zero or more.').nullish(),
    costCurrency: z.string().trim().length(3).toUpperCase().nullish(),
    /** The expense that bought it. 1:1 — one already linked is not offered. */
    transactionId: z.string().uuid().nullish(),
};
/**
 * Naming a Transaction and a cost in one request is a contradiction.
 *
 * Refused at the boundary as well as by the CHECK constraint, so the client
 * gets a 400 that names the field rather than a 500 from a constraint the
 * handler never expected to hit.
 */
const costIsSingleSourced = (value) => value.transactionId == null || (value.costMinor == null && value.purchasedOn == null);
export const createItemRequestSchema = z
    .object({
    name: itemName,
    category: z.string().trim().max(60).nullish(),
    ownerId: z.string().uuid().nullish(),
    holderId: z.string().uuid().nullish(),
    condition: z.enum(ITEM_CONDITIONS).optional(),
    notes: z.string().trim().max(ITEM_NOTES_MAX).nullish(),
    ...costFields,
})
    .refine(costIsSingleSourced, {
    message: 'An item linked to an expense takes its cost from the books.',
    path: ['costMinor'],
})
    .refine((value) => (value.costMinor == null) === (value.costCurrency == null), {
    message: 'A cost needs a currency, and a currency needs a cost.',
    path: ['costCurrency'],
});
export const updateItemRequestSchema = z
    .object({
    name: itemName.optional(),
    category: z.string().trim().max(60).nullish(),
    ownerId: z.string().uuid().nullish(),
    holderId: z.string().uuid().nullish(),
    condition: z.enum(ITEM_CONDITIONS).optional(),
    notes: z.string().trim().max(ITEM_NOTES_MAX).nullish(),
    ...costFields,
})
    .refine(costIsSingleSourced, {
    message: 'An item linked to an expense takes its cost from the books.',
    path: ['costMinor'],
})
    /**
     * An amount and a currency move together, or neither moves.
     *
     * The create schema had this and the update schema did not, so
     * `PATCH {"costCurrency":"USD"}` on an item with no cost wrote an unpaired
     * row, violated `inventory_items_cost_paired`, and answered 500 where the
     * route documents a 400. Sending exactly one of the pair is always a
     * mistake, whatever the item currently holds — which is what makes this
     * checkable here rather than only against the stored row.
     */
    .refine((item) => (item.costMinor === undefined) === (item.costCurrency === undefined) &&
    (item.costMinor == null) === (item.costCurrency == null), {
    message: 'A cost needs a currency, and a currency needs a cost.',
    path: ['costCurrency'],
});
/* ── taking kit to an Event — STORY-029 ───────────────────────────────────── */
export const assignmentSchema = z.object({
    id: z.string(),
    item: z.object({
        id: z.string(),
        name: z.string(),
        condition: z.enum(ITEM_CONDITIONS),
    }),
    event: z.object({ id: z.string(), name: z.string(), date: z.string().nullable() }),
    quantity: z.number().int(),
    returnedAt: z.string().nullable(),
    returnedBy: itemMemberSchema.nullable(),
    createdAt: z.string(),
});
/**
 * Several at once, because the loadout is a list.
 *
 * The reference workbook's equipment sheet had zero rows: the control was
 * designed and never sustained by hand. If recording a loadout takes more than
 * a few taps while loading a van it will go unused here too, so bulk is the
 * first-class path rather than a later convenience.
 */
export const assignItemsRequestSchema = z.object({
    items: z
        .array(z.object({
        itemId: z.string().uuid(),
        quantity: z.number().int().positive('More than none.').optional(),
    }))
        .min(1, 'Choose at least one thing.')
        /* One assignment per item per Event, and that has to be enforced WITHIN a
         * request as well as across them. Without this the bulk insert wrote two
         * rows with the same pair, hit the unique index, and surfaced as a 500 —
         * the guard downstream compared against a de-duplicated set and therefore
         * could not see it, and the fake has no unique index to fail on. */
        .refine((items) => new Set(items.map((entry) => entry.itemId)).size === items.length, 'The same thing cannot be on one loadout twice. Quantity covers more than one.'),
});
export const assignmentsResponseSchema = z.object({
    assignments: z.array(assignmentSchema),
});
/* ── what has not come back — STORY-030 ───────────────────────────────────── */
/**
 * Marking kit returned, optionally with the condition it came back in.
 *
 * Optional and never required: requiring it is exactly the friction that killed
 * this feature in the spreadsheet. Idempotent, because a double-tap on a phone
 * while packing down is normal — marking an already-returned assignment again
 * is a no-op, not an error and not a duplicate.
 */
export const returnItemsRequestSchema = z.object({
    assignmentIds: z.array(z.string().uuid()).min(1, 'Choose at least one thing.'),
    /** Applied to every item in the request, when given at all. */
    condition: z.enum(ITEM_CONDITIONS).optional(),
});
export const outstandingResponseSchema = z.object({
    /**
     * Kit that has not come back from an Event whose date has passed.
     *
     * Longest-out first, which is the order the question is asked in.
     */
    outstanding: z.array(assignmentSchema),
    /**
     * Kit for an Event that has not happened yet.
     *
     * A packing list, not missing equipment — and excluded from the count below,
     * because filing next month's loadout under things to chase makes the number
     * this screen exists for wrong.
     */
    packing: z.array(assignmentSchema),
    /** What `outstanding` numbers. Never includes the packing list. */
    outstandingCount: z.number().int(),
});
//# sourceMappingURL=inventory.js.map